'use client';
import { Button } from '@mui/material';
import { CopyIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

import { CenteredBGWrapper } from 'components/shared-styles-v2';
import { HOME_PATH } from 'config/internalUrlPaths';
import { ddlog } from 'utils/miscellaneous';

import * as S from './error.styled-v2';

interface Props {
  title?: string;
  message?: string;
  path?: string;
  goBack?: () => void;
  reload?: () => void;
  hideHomeButton?: boolean;
  error?: Error | null;
}

const ErrorView = ({
  title = 'Woops',
  message = "We're sorry, something went wrong",
  goBack = () => (window.location.pathname = HOME_PATH),
  reload = () => document.location.reload(),
  hideHomeButton = false,
  error,
}: Props) => {
  const [isErrorReported, setIsErrorReported] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (error && !isErrorReported) {
      setIsErrorReported(true);
      ddlog.error('error reported from woops screen', {}, error as Error);
    }
  }, [error, isErrorReported]);

  const formatErrorDetails = (err: unknown): string => {
    if (!err) {
      return '';
    }

    if (err instanceof Error) {
      return `Name: ${err.name}\nMessage: ${err.message}\n${err.stack ? `Stack: ${err.stack}` : ''}`;
    }

    if (typeof err === 'object') {
      return JSON.stringify(err, null, 2);
    }

    return String(err);
  };

  const handleCopyError = async () => {
    const errorText = formatErrorDetails(error);
    try {
      await navigator.clipboard.writeText(errorText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      ddlog.error('Failed to copy error to clipboard', { errorText }, err as Error);
    }
  };

  return (
    <CenteredBGWrapper>
      <S.ErrorContainer role="alert" aria-live="polite" aria-atomic="true">
        <S.ContentWrapper>
          <S.Title variant="h3">{title}</S.Title>
          <S.Pre variant="UILabel">{message}</S.Pre>
          <S.Buttons>
            <Button variant="outlined" onClick={reload} size="small">
              Reload
            </Button>
            {!hideHomeButton && (
              <Button variant="contained" onClick={goBack} size="small">
                Home
              </Button>
            )}
          </S.Buttons>
        </S.ContentWrapper>
        {error && (
          <S.CopyErrorButton onClick={handleCopyError} direction="row">
            {copySuccess ? 'Copied!' : 'Copy error details'} <CopyIcon />
          </S.CopyErrorButton>
        )}
      </S.ErrorContainer>
    </CenteredBGWrapper>
  );
};

export default ErrorView;
